import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import config from './config';
import { getOrganizationHeaders, withOrganizationSlug } from './orgContext';

interface Course {
  id: number;
  name: string;
}

interface Exam {
  id: number;
  name: string;
  description: string | null;
  timeLimitMinutes: number;
  isActive: boolean;
  totalQuestionCount: number;
  enabledQuestionCount: number;
  createdAt: string;
  updatedAt: string;
  courses: Course[];
}

interface AssignmentItem {
  id: number;
  examId?: number | null;
  examName?: string | null;
  type: string;
  title: string;
  description?: string | null;
  status: string;
  dueAt?: string | null;
  questionCount: number;
  maxAttempts: number;
  allowLateSubmission: boolean;
  timeLimitMinutes?: number | null;
}

type PracticeMenuItem =
  | { key: string; kind: 'assignment'; assignment: AssignmentItem }
  | { key: string; kind: 'freeExam'; exam: Exam };

export const PracticeTests: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();

  const [freeExams, setFreeExams] = useState<Exam[]>([]);
  const [examAssignments, setExamAssignments] = useState<AssignmentItem[]>([]);
  const [freeExamsLoading, setFreeExamsLoading] = useState(false);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [selectedItemKey, setSelectedItemKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const numericCourseId = courseId ? Number(courseId) : null;

  useEffect(() => {
    const controller = new AbortController();

    const fetchFreeExams = async () => {
      try {
        setFreeExamsLoading(true);
        const response = await fetch(`${config.apiBaseUrl}/api/exams`, {
          headers: {
            ...getOrganizationHeaders(),
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('Failed to load free exams.');
        }

        const data: Exam[] = await response.json();
        const scoped = numericCourseId
          ? data.filter((exam) => exam.courses.some((course) => course.id === numericCourseId))
          : data;

        if (!controller.signal.aborted) {
          setFreeExams(scoped);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error(err);
          setError((current) => current ?? 'Failed to load free exams.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setFreeExamsLoading(false);
        }
      }
    };

    void fetchFreeExams();
    return () => controller.abort();
  }, [numericCourseId]);

  useEffect(() => {
    if (!isAuthenticated || authLoading) {
      setExamAssignments([]);
      setAssignmentsLoading(false);
      return;
    }

    const controller = new AbortController();

    const fetchExamAssignments = async () => {
      try {
        setAssignmentsLoading(true);

        const token = await getAccessTokenSilently();
        const response = await fetch(`${config.apiBaseUrl}/api/assignments/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...getOrganizationHeaders(),
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('Failed to load exam assignments.');
        }

        const data = await response.json();
        const rows: AssignmentItem[] = Array.isArray(data) ? data : [];
        const assignmentExamRows = rows.filter((item) => (item.type || '').trim().toLowerCase() === 'exam');

        if (!controller.signal.aborted) {
          setExamAssignments(assignmentExamRows);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error(err);
          setError((current) => current ?? 'Failed to load assigned exam assignments.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setAssignmentsLoading(false);
        }
      }
    };

    void fetchExamAssignments();
    return () => controller.abort();
  }, [authLoading, getAccessTokenSilently, isAuthenticated]);

  const dedupedFreeExams = useMemo(() => {
    if (!freeExams.length) {
      return [];
    }

    const assignedExamIds = new Set(
      examAssignments
        .map((item) => item.examId)
        .filter((value): value is number => typeof value === 'number')
    );

    return freeExams.filter((exam) => !assignedExamIds.has(exam.id));
  }, [freeExams, examAssignments]);

  const menuItems = useMemo<PracticeMenuItem[]>(() => {
    const assignmentItems: PracticeMenuItem[] = examAssignments.map((assignment) => ({
      key: `assignment-${assignment.id}`,
      kind: 'assignment',
      assignment,
    }));

    const freeExamItems: PracticeMenuItem[] = dedupedFreeExams.map((exam) => ({
      key: `free-${exam.id}`,
      kind: 'freeExam',
      exam,
    }));

    return [...assignmentItems, ...freeExamItems];
  }, [dedupedFreeExams, examAssignments]);

  useEffect(() => {
    if (menuItems.length === 0) {
      setSelectedItemKey(null);
      return;
    }

    setSelectedItemKey((current) => {
      if (current && menuItems.some((item) => item.key === current)) {
        return current;
      }

      return menuItems[0].key;
    });
  }, [menuItems]);

  const selectedItem = useMemo(
    () => menuItems.find((item) => item.key === selectedItemKey) ?? null,
    [menuItems, selectedItemKey]
  );

  const loading = freeExamsLoading || (isAuthenticated && (authLoading || assignmentsLoading));

  const selectedAssignment = selectedItem?.kind === 'assignment' ? selectedItem.assignment : null;
  const selectedFreeExam = selectedItem?.kind === 'freeExam' ? selectedItem.exam : null;
  const selectedTitle = selectedAssignment
    ? (selectedAssignment.examName || selectedAssignment.title)
    : (selectedFreeExam?.name ?? null);
  const selectedDescription = selectedAssignment?.description || selectedFreeExam?.description || null;
  const selectedQuestionCount = selectedAssignment?.questionCount ?? selectedFreeExam?.totalQuestionCount ?? 0;
  const selectedTimeLimit = selectedAssignment?.timeLimitMinutes ?? selectedFreeExam?.timeLimitMinutes ?? null;

  const startDisabled = !selectedItem
    || (selectedItem.kind === 'assignment' && !selectedItem.assignment.examId)
    || selectedQuestionCount === 0;

  const handleStartSelected = () => {
    if (!selectedItem) {
      return;
    }

    if (selectedItem.kind === 'freeExam') {
      navigate(withOrganizationSlug(`/exam-center/${selectedItem.exam.id}`));
      return;
    }

    if (selectedItem.assignment.examId) {
      navigate(withOrganizationSlug(`/exam-center/${selectedItem.assignment.examId}`));
    }
  };

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-gradient-to-br from-app-page-from to-app-page-to">
      <div className="app-breadcrumb-bar">
        <div className="app-breadcrumb-row">
          <button onClick={() => navigate(withOrganizationSlug('/courses'))} className="app-breadcrumb-link">Courses</button>
          <span className="app-breadcrumb-sep">›</span>
          <span className="app-breadcrumb-current">Practice Tests</span>
          {numericCourseId && (
            <>
              <span className="app-breadcrumb-sep">›</span>
              <span className="app-breadcrumb-trailing truncate">Course {numericCourseId}</span>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 w-full px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 overflow-auto lg:overflow-hidden">
        {error && (
          <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-3 py-3 text-sm text-red-700 sm:mb-6 sm:px-4 sm:text-base">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4 lg:h-full lg:min-h-0 items-stretch">
          <div className="lg:col-span-4 lg:h-full lg:min-h-0">
            <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col lg:h-full lg:min-h-0">
              <div className="px-3 py-2.5 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center text-lg shrink-0">
                    📚
                  </div>
                  <div className="min-w-0 text-left">
                    <p className="text-sm font-bold text-gray-900 leading-tight">Exams</p>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-h-0 p-2 md:p-2.5 space-y-3 overflow-y-auto">
                <section className="rounded-lg border border-indigo-100">
                  <div className="px-3 py-2 border-b border-indigo-100 bg-indigo-50/60 text-left">
                    <p className="text-xs font-bold uppercase tracking-wider text-indigo-700 text-left">Assigned Exam Assignments</p>
                    <p className="text-xs text-indigo-500 text-left">{examAssignments.length} assigned</p>
                  </div>

                  <div className="space-y-2 p-2">
                    {authLoading || assignmentsLoading ? (
                      <div className="py-3 text-center text-xs text-indigo-500">Loading assigned exams...</div>
                    ) : !isAuthenticated ? (
                      <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-3 text-xs text-indigo-700">
                        Sign in to view your assigned exam assignments.
                      </div>
                    ) : examAssignments.length === 0 ? (
                      <div className="rounded-md border border-dashed border-gray-200 px-3 py-3 text-xs text-gray-500">
                        No exam assignments available for your user.
                      </div>
                    ) : (
                      examAssignments.map((assignment) => {
                        const itemKey = `assignment-${assignment.id}`;
                        const isSelected = selectedItemKey === itemKey;

                        return (
                          <button
                            key={itemKey}
                            type="button"
                            onClick={() => setSelectedItemKey(itemKey)}
                            className={`w-full rounded-lg border px-3 py-2 text-left transition-all ${
                              isSelected
                                ? 'border-indigo-300 bg-indigo-50 shadow-sm'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <p className={`text-sm font-semibold truncate ${isSelected ? 'text-indigo-900' : 'text-slate-800'}`}>
                              {assignment.examName || assignment.title}
                            </p>
                            <p className="mt-0.5 text-xs text-slate-500 truncate">{assignment.title}</p>
                            <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-500">
                              <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-indigo-700">Assignment</span>
                              {assignment.questionCount > 0 && <span>{assignment.questionCount} questions</span>}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </section>

                <section className="rounded-lg border border-emerald-100">
                  <div className="px-3 py-2 border-b border-emerald-100 bg-emerald-50/60 text-left">
                    <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 text-left">Organization Free Exams</p>
                    <p className="text-xs text-emerald-600 text-left">{dedupedFreeExams.length} available</p>
                  </div>

                  <div className="space-y-2 p-2">
                    {freeExamsLoading ? (
                      <div className="py-3 text-center text-xs text-emerald-600">Loading free exams...</div>
                    ) : dedupedFreeExams.length === 0 ? (
                      <div className="rounded-md border border-dashed border-gray-200 px-3 py-3 text-xs text-gray-500">
                        No free exams available for this organization.
                      </div>
                    ) : (
                      dedupedFreeExams.map((exam) => {
                        const itemKey = `free-${exam.id}`;
                        const isSelected = selectedItemKey === itemKey;

                        return (
                          <button
                            key={itemKey}
                            type="button"
                            onClick={() => setSelectedItemKey(itemKey)}
                            className={`w-full rounded-lg border px-3 py-2 text-left transition-all ${
                              isSelected
                                ? 'border-emerald-300 bg-emerald-50 shadow-sm'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <p className={`text-sm font-semibold truncate ${isSelected ? 'text-emerald-900' : 'text-slate-800'}`}>
                              {exam.name}
                            </p>
                            <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-500">
                              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-700">Free Exam</span>
                              <span>{exam.totalQuestionCount} questions</span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </section>

                {!isAuthenticated && (
                  <div className="p-3 rounded-xl border border-indigo-300 bg-gradient-to-br from-indigo-50 to-blue-50 shadow-sm">
                    <p className="text-sm font-bold text-indigo-900 text-center mb-2">Sign in for Assigned Exams</p>
                    <p className="text-xs text-indigo-700 text-center mb-3">Free exams are available now. Assigned exam assignments appear after login.</p>
                    <button
                      onClick={() =>
                        loginWithRedirect({
                          appState: {
                            returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                          },
                        })
                      }
                      className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-700 px-3 py-2.5 text-xs font-semibold text-white transition-colors"
                    >
                      Login / Sign Up
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 lg:h-full lg:min-h-0">
            <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col lg:h-full lg:min-h-0">
              {loading ? (
                <div className="flex-1 min-h-0 px-6 py-8 text-center flex flex-col items-center justify-center">
                  <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
                  <p className="text-base font-semibold text-slate-700">Loading practice items...</p>
                  <p className="mt-1 text-sm text-slate-500">Preparing assigned and free exam data.</p>
                </div>
              ) : !selectedItem ? (
                <div className="flex-1 min-h-0 p-8 flex items-center justify-center">
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 py-10 px-6 text-center max-w-lg">
                    <div className="mb-3 text-4xl">Menu</div>
                    <p className="text-base font-semibold text-slate-700 sm:text-lg">
                      Select an exam item from the left menu.
                    </p>
                    <p className="mt-2 text-sm text-slate-500">Details and start action will appear here.</p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-h-0 p-3 sm:p-4 md:p-5 lg:overflow-y-auto">
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
                    <div className="bg-gradient-to-r from-app-header-from to-app-header-to px-4 py-3 text-white">
                      <div className="flex flex-wrap items-center gap-2 text-left">
                        <span className="text-base font-bold sm:text-lg leading-tight truncate mr-2">{selectedTitle}</span>
                        {selectedItem.kind === 'assignment' ? (
                          <span className="inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold">Assigned Exam</span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold">Free Exam</span>
                        )}
                      </div>

                      {selectedAssignment?.examName && selectedAssignment.title !== selectedAssignment.examName && (
                        <p className="mt-1 text-xs text-app-header-muted">Assignment: {selectedAssignment.title}</p>
                      )}

                      <p className="mt-1 text-xs text-app-header-muted leading-snug">
                        {selectedDescription || 'No description available'}
                      </p>
                    </div>

                    <div className="p-4 sm:p-5">
                      <div className="mb-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Questions</p>
                          <p className="mt-1 text-lg font-bold text-slate-800">{selectedQuestionCount}</p>
                        </div>
                        <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Time Limit</p>
                          <p className="mt-1 text-lg font-bold text-slate-800">
                            {selectedTimeLimit && selectedTimeLimit > 0 ? `${selectedTimeLimit} min` : 'Not set'}
                          </p>
                        </div>
                        <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Type</p>
                          <p className="mt-1 text-lg font-bold text-slate-800">
                            {selectedItem.kind === 'assignment' ? 'Exam Assignment' : 'Free Practice Exam'}
                          </p>
                        </div>
                      </div>

                      {selectedAssignment?.dueAt && (
                        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                          Due: {new Date(selectedAssignment.dueAt).toLocaleString()}
                        </div>
                      )}

                      {selectedFreeExam && selectedFreeExam.courses.length > 0 && (
                        <div className="mb-4">
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Courses</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedFreeExam.courses.map((course) => (
                              <span key={course.id} className="rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-700">
                                {course.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
                        <div className="text-sm text-slate-600">
                          {selectedItem.kind === 'assignment'
                            ? 'This exam was assigned to you. Start from the linked exam definition.'
                            : 'This is a free organization practice exam.'}
                        </div>
                        <button
                          onClick={handleStartSelected}
                          disabled={startDisabled}
                          className="w-full rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
                        >
                          {selectedItem.kind === 'assignment' ? 'Start Assigned Exam' : 'Start Free Exam'}
                        </button>
                      </div>

                      {selectedItem.kind === 'assignment' && !selectedItem.assignment.examId && (
                        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                          This assignment is missing a linked exam definition. Contact your teacher/admin.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeTests;
